import React, { useEffect, useState } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { api } from "../lib/api";

export default function MoneyFlow() {
  const [sneer, setSneer] = useState(null);
  const [flow, setFlow] = useState(null);

  useEffect(() => {
    api.get("/sneer").then(r => setSneer(r.data));
    api.get("/money-flow").then(r => setFlow(r.data));
  }, []);

  return (
    <div data-testid="page-money-flow">
      <section className="force-dark relative overflow-hidden">
        <div className="bg-grid-dark absolute inset-0 opacity-60" />
        <div className="container-rp py-16 lg:py-24 relative">
          <div className="overline">Money Flow Intelligence Hub</div>
          <h1 className="font-display text-5xl lg:text-7xl font-bold mt-4 tracking-tight">
            Capital. Currents.<br />
            <span style={{ color: "var(--rp-accent)" }}>Corridors.</span>
          </h1>
          <p className="text-slate-300 mt-6 max-w-2xl text-lg">
            Singapore is the gateway. RiskPedia translates MAS monetary policy, FX flows, payment corridor caps and BIS Project Nexus into operating intelligence for BFSI desks.
          </p>
        </div>
      </section>

      {/* S$NEER */}
      <section className="section">
        <div className="container-rp">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <div className="overline">S$NEER · MAS exchange-rate-based policy</div>
              <h2 className="font-display text-4xl font-semibold mt-3">The Singapore Dollar Nominal Effective Exchange Rate.</h2>
              <p className="text-slate-600 mt-4 leading-relaxed">
                Unlike most central banks, MAS conducts monetary policy through the SGD's exchange rate — not interest rates. The S$NEER trades within an undisclosed policy band whose slope, mid-point and width are reviewed twice yearly at the Monetary Policy Statement (MPS).
              </p>
              {sneer && (
                <div className="mt-8 grid grid-cols-3 gap-4">
                  <KV label="Current" value={sneer.current.toFixed(2)} />
                  <KV label="30-day change" value={`${sneer.change_30d_bps >= 0 ? "+" : ""}${sneer.change_30d_bps} bps`} accent={sneer.change_30d_bps >= 0 ? "emerald" : "rose"} />
                  <KV label="Next MPS" value={sneer.next_mps} />
                </div>
              )}
              {sneer && (
                <div className="rp-card p-6 mt-6">
                  <div className="overline-muted mb-2">Latest stance</div>
                  <p className="text-sm text-slate-700 leading-relaxed">{sneer.mas_stance}</p>
                  <div className="grid grid-cols-3 gap-3 mt-4 text-xs">
                    <div><span className="text-slate-500">Band width</span><div className="font-mono font-semibold">±{sneer.policy_band.width_pct}%</div></div>
                    <div><span className="text-slate-500">Mid-point</span><div className="font-mono font-semibold">{sneer.policy_band.midpoint_unchanged ? "Unchanged" : "Re-centred"}</div></div>
                    <div><span className="text-slate-500">Slope</span><div className="font-mono font-semibold text-xs">{sneer.policy_band.slope}</div></div>
                  </div>
                </div>
              )}
            </div>

            <div className="lg:col-span-5">
              {sneer && (
                <div className="rp-card p-6">
                  <div className="overline">30-day trajectory</div>
                  <div className="mt-6 h-48 flex items-end gap-px">
                    {sneer.series.map((p, i) => {
                      const min = Math.min(...sneer.series.map(x => x.value));
                      const max = Math.max(...sneer.series.map(x => x.value));
                      const range = Math.max(0.001, max - min);
                      const h = 10 + ((p.value - min) / range) * 170;
                      return (
                        <div key={i} className="flex-1 transition-all" style={{ height: `${h}px`, background: i === sneer.series.length - 1 ? "var(--rp-accent)" : "var(--rp-primary)", opacity: i === sneer.series.length - 1 ? 1 : 0.4 + (i / sneer.series.length) * 0.5 }} title={`${p.date} · ${p.value}`} />
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-3 text-[10px] font-mono text-slate-400">
                    <span>30 days ago</span>
                    <span>today</span>
                  </div>
                </div>
              )}

              {/* FX */}
              {flow && (
                <div className="rp-card p-6 mt-6">
                  <div className="overline">FX Pulse · SGD crosses</div>
                  <div className="mt-4 space-y-2">
                    {Object.entries(flow.fx_pulse).map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between text-sm">
                        <span className="font-mono">{k}</span>
                        <div className="flex items-center gap-3">
                          <span className="font-mono font-semibold">{v.value}</span>
                          <span className={`font-mono text-xs flex items-center gap-1 ${v.change_pct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                            {v.change_pct >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                            {v.change_pct >= 0 ? "+" : ""}{v.change_pct}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Corridors */}
      <section className="section bg-slate-50 border-y border-slate-200">
        <div className="container-rp">
          <div className="overline">Cross-border payment corridors</div>
          <h2 className="font-display text-4xl font-semibold mt-3">ASEAN-5 + India. In real time.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 mt-10">
            {flow?.corridors?.map(c => (
              <div key={c.name} className="bg-white p-6">
                <div className="font-display text-xl font-semibold">{c.name}</div>
                <div className="text-[10px] font-mono text-emerald-600 mt-1">● {c.status}</div>
                <div className="font-display text-3xl font-bold mt-4" style={{ color: "var(--rp-primary)" }}>S${c.volume_sgd_m}m</div>
                <div className="text-xs text-slate-500 mt-1">monthly volume</div>
                <div className="text-xs font-mono text-emerald-600 mt-2">+{c.growth_yoy_pct}% YoY</div>
              </div>
            ))}
          </div>

          {flow?.insights && (
            <div className="mt-10 rp-card p-8">
              <div className="overline">Intelligence brief</div>
              <ul className="mt-4 space-y-3">
                {flow.insights.map((i, idx) => (
                  <li key={idx} className="text-sm text-slate-700 flex gap-3 leading-relaxed">
                    <span className="font-mono text-xs" style={{ color: "var(--rp-accent)" }}>{String(idx + 1).padStart(2, "0")}</span>
                    {i}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function KV({ label, value, accent }) {
  const cls = accent === "emerald" ? "text-emerald-600" : accent === "rose" ? "text-rose-600" : "text-slate-900";
  return (
    <div className="rp-card p-4">
      <div className="overline-muted">{label}</div>
      <div className={`font-display text-2xl font-bold mt-2 ${cls}`}>{value}</div>
    </div>
  );
}
